import type { Compania } from "@/componentes/identidad/identidad"

/**
 * Los cinco formatos de rótulo, del lado del navegador.
 *
 * ⚠ **Espejo de `schemas/rotulacion.py`, y a propósito NO se genera.**
 * `api/esquema.d.ts` sí sale de OpenAPI, pero allí `datos` llega como la unión sin
 * discriminar —Pydantic la publica como `anyOf`— y TypeScript no puede estrechar un
 * `anyOf` por el campo `tipo` sin ayuda. Estos tipos son esa ayuda: lo que permite
 * que el editor de cada formato reciba su forma y no `unknown`.
 *
 * Si cambia el esquema del backend, cambia aquí. La prueba
 * `rotulacion.test.tsx` comprueba que los campos obligatorios de cada formato siguen
 * siendo los mismos, para que la divergencia salte en la suite y no al imprimir.
 */

export type TipoRotulo = "PESTANA" | "ETIQUETA_CARPETA" | "LOMO" | "CARATULA" | "TEJUELO"

export type SeccionEtiqueta = {
  descripcion: string
}

export type DatosPestana = {
  tipo: "PESTANA"
  compania: Compania
  departamento: string | null
  contenido: string
  /** El código de archivo: «FI–012». */
  siglas: string | null
  periodo: string | null
}

export type DatosEtiquetaCarpeta = {
  tipo: "ETIQUETA_CARPETA"
  compania: Compania
  periodo: string | null
  empresa: string
  siglas: string
  nombre_expediente: string
  seccion_archivo: string
  secciones: SeccionEtiqueta[]
}

export type DatosLomo = {
  tipo: "LOMO"
  compania: Compania
  /** El contenido, en grande y de abajo hacia arriba. */
  titulo: string
  /** El departamento, en versales bajo el código. */
  subtitulo: string | null
  /** El código de archivo: «FI–012». */
  siglas: string | null
  /** Qué hay dentro. Una frase, no una lista: la tira mide 75 mm de ancho. */
  contenido: string | null
  /**
   * El período que cubre la carpeta.
   *
   * ⚠ **Es TEXTO y no una fecha**, como el `periodo` de la carátula. En el lomo de
   * un archivo se escribe un rango o un mes —«2024 – 2026», «ENERO – JUNIO 2026»— y
   * un tipo fecha obliga a elegir un día que la carpeta no tiene.
   */
  fecha: string | null
  /** El canto de la carpeta física. Ver `medidas.ts`. */
  ancho_mm: number
  /** El largo de la tira. */
  alto_mm: number
  /**
   * Cómo corre el contenido (03/11): de abajo hacia arriba —el manual— o de frente. Lo
   * guardado antes no lo trae y sale vertical.
   */
  orientacion?: "VERTICAL" | "HORIZONTAL"
}

export type DatosCaratula = {
  tipo: "CARATULA"
  compania: Compania
  hoja: "CARTA" | "OFICIO"
  codigo: string | null
  departamento: string | null
  seccion: string | null
  responsable: string | null
  ubicacion: string | null
  titulo: string
  subtitulo: string | null
  entidad: string | null
  rif: string | null
  periodo: string | null
  con_membrete: boolean
}

export type DatosTejuelo = {
  tipo: "TEJUELO"
  compania: Compania
  /** Sobre el título: «Libro N.º 3 · AD–001». */
  codigo: string | null
  /** Bajo el título: «Junta de socios · 2024 – 2026». */
  detalle: string | null
  empresa: string
  libro: string
  rif: string | null
}

export type DatosRotulo =
  | DatosPestana
  | DatosEtiquetaCarpeta
  | DatosLomo
  | DatosCaratula
  | DatosTejuelo

export type Rotulo = {
  id: number
  tipo: TipoRotulo
  titulo: string
  datos: DatosRotulo
  cliente_id: number | null
  cliente_nombre: string | null
  expediente_id: number | null
  expediente_numero: string | null
  creado_en: string
  actualizado_en: string
}

/** Lo que devuelve `GET /rotulos/precarga`: sugerencias sueltas, todas opcionales. */
export type Precarga = {
  cliente_id?: number
  empresa?: string
  entidad?: string
  rif?: string
  siglas?: string
  expediente_numero?: string
  nombre_expediente?: string
}

/**
 * Un rótulo recién nacido de cada formato.
 *
 * ⚠ **Los valores por defecto NO están vacíos donde el papel no admite vacío.**
 * `seccion_archivo` nace en «LEGAL» porque es la sección con la que empieza casi todo
 * el archivo, y una etiqueta con ese hueco en blanco se imprime igual y hay que
 * tirarla. Lo que sí nace vacío es lo que sólo sabe quien rotula.
 */
export function rotuloEnBlanco(tipo: TipoRotulo): DatosRotulo {
  const compania = "SETIECA_AGENTES"
  switch (tipo) {
    case "PESTANA":
      return { tipo, compania, departamento: null, contenido: "", siglas: null, periodo: null }
    case "ETIQUETA_CARPETA":
      return {
        tipo,
        compania,
        periodo: null,
        empresa: "",
        siglas: "",
        nombre_expediente: "",
        seccion_archivo: "LEGAL",
        secciones: [],
      }
    case "LOMO":
      return {
        tipo,
        compania,
        titulo: "",
        subtitulo: null,
        siglas: null,
        contenido: null,
        fecha: null,
        ancho_mm: 60,
        alto_mm: 180,
        orientacion: "VERTICAL",
      }
    case "CARATULA":
      return {
        tipo,
        compania,
        hoja: "CARTA",
        codigo: null,
        departamento: null,
        seccion: null,
        responsable: null,
        ubicacion: null,
        titulo: "",
        subtitulo: null,
        entidad: null,
        rif: null,
        periodo: null,
        con_membrete: true,
      }
    case "TEJUELO":
      return {
        tipo,
        compania,
        codigo: null,
        detalle: null,
        empresa: "",
        libro: "Libro de actas",
        rif: null,
      }
  }
}
